import client from './apiClient';
import {
  CreateOrUpdateWaterfallVersionViewModel,
  CreateOrUpdateWaterfallViewModel,
  WaterfallVersionViewModel,
  WaterfallViewModel,
  StepViewModel,
  WaterfallRuleViewModel,
  WaterfallTraceRecordViewModel,
  WaterfallExecutionRecordViewModel,
  IntegrationType,
  WaterfallMigrationDiff,
  WaterfallMigrationImportRequest,
  WaterfallMigrationImportResult,
} from '../models/waterfall';
import { SimpleBinaryExpression, SimpleExpression } from '../models/expression';
import * as expressionService from './expressionService';
import * as commonService from './commonService';

// ===== Sanitization Helpers =====

const stripRightValueFormat = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .replace(/^["']/, '')
    .replace(/["']$/, '')
    .trim();
};

const sanitizeBinaryExpression = (expr: SimpleBinaryExpression): SimpleBinaryExpression => {
  return {
    ...expr,
    right: expr.right?.map(val => stripRightValueFormat(val)) || null,
  };
};

const sanitizeExpression = (expr: SimpleExpression | null | undefined): SimpleExpression | null => {
  if (!expr) return null;
  
  return {
    ...expr,
    expressions: expr.expressions?.map((item) => ({
      ...item,
      expression: item.expression ? sanitizeBinaryExpression(item.expression) : null,
      expressionGroup: item.expressionGroup ? sanitizeExpression(item.expressionGroup) : null,
    })) || null,
  };
};

const sanitizeRule = (rule: WaterfallRuleViewModel): WaterfallRuleViewModel => {
  return {
    ...rule,
    expression: sanitizeExpression(rule.expression),
  };
};

const sanitizeStep = (step: StepViewModel): StepViewModel => {
  return {
    ...step,
    waterfallRules: step.waterfallRules?.map(sanitizeRule) || null,
    nextSteps: step.nextSteps?.map(sanitizeStep) || null,
  };
};

const sanitizeVersion = (version: WaterfallVersionViewModel): WaterfallVersionViewModel => {
  return {
    ...version,
    initialSteps: version.initialSteps?.map(sanitizeStep) || [],
  };
};

const sanitizeWaterfall = (waterfall: WaterfallViewModel): WaterfallViewModel => {
  return {
    ...waterfall,
    currentVersion: waterfall.currentVersion ? sanitizeVersion(waterfall.currentVersion) : null,
    versions: waterfall.versions?.map(sanitizeVersion) || null,
  };
};

export const getAll = async (includeVersions: boolean = false): Promise<WaterfallViewModel[]> => {
  const { data } = await client.get<WaterfallViewModel[]>('/waterfall', {
    params: { includeVersions },
  });
  return data.map(sanitizeWaterfall);
};

export const get = async (id: number, includeVersions: boolean = true): Promise<WaterfallViewModel> => {
  const { data } = await client.get<WaterfallViewModel>(`/waterfall/${id}`, {
    params: { includeVersions },
  });
  return sanitizeWaterfall(data);
};

export const deleteWaterfall = async (id: number): Promise<void> => {
  await client.delete(`/waterfall/${id}`);
};

export const create = async (payload: CreateOrUpdateWaterfallViewModel): Promise<WaterfallViewModel> => {
  const { data } = await client.post<WaterfallViewModel>('/waterfall', payload);
  return sanitizeWaterfall(data);
};

export const update = async (payload: CreateOrUpdateWaterfallViewModel): Promise<WaterfallViewModel> => {
  const { data } = await client.put<WaterfallViewModel>('/waterfall', payload);
  return sanitizeWaterfall(data);
};

export const getAllVersions = async (waterfallId: number): Promise<WaterfallVersionViewModel[]> => {
  const { data } = await client.get<WaterfallVersionViewModel[]>(`/waterfalls/${waterfallId}/versions`);
  return data.map(sanitizeVersion);
};

export const getVersion = async (waterfallId: number, waterfallVersionId: number): Promise<WaterfallVersionViewModel> => {
  const { data } = await client.get<WaterfallVersionViewModel>(`/waterfalls/${waterfallId}/versions/${waterfallVersionId}`);
  return sanitizeVersion(data);
};

export const createVersion = async (
  waterfallId: number,
  payload: CreateOrUpdateWaterfallVersionViewModel
): Promise<WaterfallVersionViewModel> => {
  const { data } = await client.post<WaterfallVersionViewModel>(`/waterfalls/${waterfallId}/versions`, payload);
  return sanitizeVersion(data);
};

export const updateVersion = async (
  waterfallId: number,
  payload: CreateOrUpdateWaterfallVersionViewModel
): Promise<WaterfallVersionViewModel> => {
  const { data } = await client.put<WaterfallVersionViewModel>(`/waterfalls/${waterfallId}/versions`, payload);
  return sanitizeVersion(data);
};

export const deleteVersion = async (waterfallId: number, waterfallVersionId: number): Promise<void> => {
  await client.delete(`/waterfalls/${waterfallId}/versions/${waterfallVersionId}`);
};

// ===== Waterfall Operations =====

export const abortWaterfallExecution = async (waterfallExecutionRecordId: number): Promise<void> => {
  await client.put(`/waterfall/abort/${waterfallExecutionRecordId}`);
};

export const setCurrentVersion = async (waterfallId: number, currentVersionId: number): Promise<WaterfallViewModel> => {
  const { data } = await client.put<WaterfallViewModel>(`/waterfall/${waterfallId}/${currentVersionId}`);
  return sanitizeWaterfall(data);
};

export const reorderWaterfalls = async (integrationType: IntegrationType, waterfallIds: number[]): Promise<void> => {
  await client.put(`/waterfall/reorder/${integrationType}`, { waterfallIds });
};

export const getWaterfallTrace = async (
  integrationHistoryId: number,
  waterfallId: number
): Promise<WaterfallTraceRecordViewModel[]> => {
  const { data } = await client.get<WaterfallTraceRecordViewModel[]>(
    `/waterfall/trace/${integrationHistoryId}/${waterfallId}`
  );
  return data;
};

export const archiveWaterfall = async (waterfallId: number, archived: boolean): Promise<WaterfallViewModel> => {
  const { data } = await client.put<WaterfallViewModel>(`/waterfall/${waterfallId}/archive`, null, {
    params: { archived },
  });
  return sanitizeWaterfall(data);
};

export const setDisabledStatus = async (waterfallId: number, disabled: boolean): Promise<WaterfallViewModel> => {
  const { data } = await client.put<WaterfallViewModel>(`/waterfall/${waterfallId}/disabled`, null, {
    params: { disabled },
  });
  return sanitizeWaterfall(data);
};

// ===== Waterfall Execution Records =====

export const getWaterfallExecutionRecord = async (
  waterfallExecutionRecordId: number,
  includeFiles: boolean = false
): Promise<WaterfallExecutionRecordViewModel> => {
  const { data } = await client.get<WaterfallExecutionRecordViewModel>(
    `/waterfall-execution-records/${waterfallExecutionRecordId}`,
    {
      params: { includeFiles },
    }
  );
  return data;
};

// ===== Version Execution Check =====

export const getVersionIdsWithExecutionRecords = async (waterfallId: number): Promise<number[]> => {
  const { data } = await client.get<number[]>(`/waterfalls/${waterfallId}/versions/execution-check`);
  return data;
};

// ===== Migration =====

export const exportMigration = async (): Promise<Blob> => {
  const { data } = await client.get('/waterfall/migration/export', {
    responseType: 'blob',
  });
  return data;
};

export const prepareImportMigration = async (file: File): Promise<WaterfallMigrationDiff> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post<WaterfallMigrationDiff>('/waterfall/migration/prepare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const importMigration = async (request: WaterfallMigrationImportRequest): Promise<WaterfallMigrationImportResult> => {
  const { data } = await client.post<WaterfallMigrationImportResult>('/waterfall/migration/import', request);
  return data;
};

// ===== Re-exported Services =====

export const getFieldMetadata = expressionService.getFieldMetadata;
export const getOperators = expressionService.getOperators;
export const getEnumValues = commonService.getEnumValues;
